import { useEffect, useState } from "react";
import { FiPlus, FiToggleLeft, FiToggleRight } from "react-icons/fi";
import bankService from "../../../services/bank.service";
import DataTableToolbar from "../../../components/common/DataTableToolbar";
import StatusFilterSelect from "../../../components/common/StatusFilterSelect";
import { printTablePage } from "../../../utils/printTable";
import "../../../styles/users.css";

const initial = {
  name: "",
  email: "",
  phone: "",
  password: "",
  branchName: "",
  employeeNumber: "",
};

export default function BankPersons() {
  const [persons, setPersons] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [registrationId, setRegistrationId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => setResendCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const load = async () => {
    try {
      const params =
        statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const res = await bankService.getPersons(params);
      setPersons(res?.data?.data?.persons || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      const response = await bankService.createPerson(form);
      const body = response?.data ?? response;
      const payload = body?.data && typeof body.data === "object" ? body.data : body;
      const nextRegistrationId = String(payload?.registrationId || "").trim();
      if (!nextRegistrationId) {
        setError("OTP was sent but the registration session was not returned. Please try again.");
        return;
      }
      setRegistrationId(nextRegistrationId);
      setResendCooldown(Number(payload?.resendAvailableInSeconds || 60));
      setOtpSent(Boolean(body?.requiresOtp || nextRegistrationId));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create person");
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setError("Enter the 6-digit OTP received by SMS"); return; }
    setSaving(true);
    setError("");
    try {
      await bankService.verifyPersonOtp(registrationId, otp);
      setForm(initial);
      setShowForm(false);
      setOtpSent(false);
      setRegistrationId("");
      setOtp("");
      setResendCooldown(0);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setSaving(false);
    }
  };

  const handleResendOtp = async () => {
    if (saving || resendCooldown > 0) return;
    setSaving(true);
    setError("");
    try {
      const response = await bankService.resendPersonOtp(registrationId);
      const body = response?.data ?? response;
      const payload = body?.data && typeof body.data === "object" ? body.data : body;
      setResendCooldown(Number(payload?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP");
    } finally {
      setSaving(false);
    }
  };

  const backFromOtp = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const handleToggle = async (id) => {
    try {
      await bankService.togglePerson(id);
      await load();
    } catch {/* silent */}
  };

  return (
    <div className="page">
      <div className="users-actions">
        <h2>Bank / NBFC Executive</h2>
      </div>

      <DataTableToolbar
        onPrint={printTablePage}
        leading={
          <StatusFilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            ariaLabel="Filter bank persons by status"
          />
        }
      >
        <button
          type="button"
          className="primary-page-btn"
          onClick={() => setShowForm((s) => !s)}
        >
          <FiPlus /> {showForm ? "Cancel" : "Add Person"}
        </button>
      </DataTableToolbar>

      {showForm && (!otpSent ? (
        <form onSubmit={handleCreate} style={{ background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 8, padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>New Bank Person</h3>
          <div className="form-grid two-column">
            <div className="form-group">
              <label>Name *</label>
              <input name="name" value={form.name} onChange={handleChange} required placeholder="Full name" />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="Email" />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="Phone number" />
            </div>
            <div className="form-group">
              <label>Password * (min 8)</label>
              <input type="password" name="password" value={form.password} onChange={handleChange} required minLength={8} placeholder="Password" autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input name="branchName" value={form.branchName} onChange={handleChange} placeholder="Branch name" />
            </div>
            <div className="form-group">
              <label>Employee number</label>
              <input name="employeeNumber" value={form.employeeNumber} onChange={handleChange} placeholder="Employee / staff ID" />
            </div>
          </div>
          {error && <p className="error-text">{error}</p>}
          <button type="submit" className="primary-page-btn" disabled={saving} style={{ marginTop: 12 }}>
            {saving ? "Creating..." : "Create Person"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} style={{ background: "var(--card-bg, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 8, padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Verify Person's Mobile Number</h3>
          <p className="muted">We sent a 6-digit OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.</p>
          <div className="form-group">
            <label htmlFor="bank-person-otp">Enter OTP</label>
            <input id="bank-person-otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" />
          </div>
          {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
          {error && <p className="error-text">{error}</p>}
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <button type="button" className="secondary-page-btn" onClick={backFromOtp} disabled={saving}>Back</button>
            <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={saving || resendCooldown > 0}>Resend OTP</button>
            <button type="submit" className="primary-page-btn" disabled={saving || otp.length !== 6}>{saving ? "Verifying..." : "Verify OTP"}</button>
          </div>
        </form>
      ))}

      {loading ? (
        <p className="muted">Loading...</p>
      ) : persons.length === 0 ? (
        <p className="muted">No persons match this status filter.</p>
      ) : (
        <div className="company-table-wrap printable-table-area">
          <table className="users-table excel-grid-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Branch</th>
                <th>Employee number</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {persons.map((p) => (
                <tr key={p._id}>
                  <td>{p.name || "—"}</td>
                  <td>{p.email || "—"}</td>
                  <td>{p.phone || "—"}</td>
                  <td>{p.branchName || "—"}</td>
                  <td>{p.employeeNumber || "—"}</td>
                  <td>
                    <span
                      className={`company-status company-status--${
                        p.isActive ? "active" : "inactive"
                      }`}
                    >
                      {p.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="company-actions-td">
                    <div className="company-actions-cell">
                      <button
                        type="button"
                        className={`company-btn ${
                          p.isActive ? "company-btn--block" : "company-btn--unblock"
                        }`}
                        onClick={() => handleToggle(p._id)}
                        title={p.isActive ? "Deactivate" : "Activate"}
                      >
                        {p.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                        <span>{p.isActive ? "Deactivate" : "Activate"}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
